import {
  Button,
  Card,
  CardContent,
  ContextMenu,
  ContextMenuItem,
  DataTable,
  Skeleton,
  Text,
} from "@/components/ui";
import { ContactPortalInviteDialog } from "@/components/features/contact/ContactPortalInviteDialog";
import { EditContactModal } from "@/components/features/contact/AddOrEditContactModal/EditContactModal";
import { ServerErrorFallbackCard } from "@/components/ui/ServerErrorFallback/ServerErrorFallbackCard";
import { useUser } from "@/components/UserProvider";
import { useContactPortalAccess } from "@/lib/data/react-query/contacts/contact-portal-access";
import { retrieveContactQueryOptions } from "@/lib/data/react-query/contacts/contact";
import {
  useCancelMatterSurvey,
  useDeleteMatterSurvey,
  useMatterSurvey,
  useMatterSurveys,
} from "@/lib/data/react-query/matters/matter-survey";
import type { MatterSurvey } from "@/lib/data/requests/matters/matter-survey";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
  PAGE_PERMISSION_KEYS,
} from "@/lib/data/static/permissionOptions";
import { tanstackTableNames } from "@/lib/data/static/tanstack-table-names";
import type { Matter } from "@/lib/types";
import { formatContactsName } from "@/lib/utils/format";
import {
  getMatterPartyConnections,
  toMatterContactConnectionPresentation,
} from "@/lib/utils/connections/matter-party-connections";
import { isClientPartyConnectionLabel } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { ErrorBoundary } from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { Ban, CircleAlert, Eye, Pencil, PlusCircle, Send } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  MatterSurveyActionDialog,
  type MatterSurveyAction,
} from "./MatterSurveyActionDialog";
import { MatterSurveyCreateModal } from "./MatterSurveyCreateModal";
import { MatterSurveyResultsDialog } from "./MatterSurveyResultsDialog";
import { matterSurveyTableColumns } from "./matter-survey-table-columns";

function MatterInformationSurveySectionContent({
  matter,
  requestedSurveyId,
}: {
  matter: Matter;
  requestedSurveyId?: string;
}) {
  const { hasActionPermission, hasPagePermission } = useUser();
  const queryClient = useQueryClient();
  const canView = hasPagePermission(PAGE_PERMISSION_KEYS.mattersDetailSurveys);
  const canCreate = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterSurvey,
    ACTION_PERMISSION_VALUES.create,
  );
  const canInvite = hasActionPermission(
    ACTION_PERMISSION_KEYS.contactPortalAccess,
    ACTION_PERMISSION_VALUES.create,
  );
  const canEditContact = hasActionPermission(
    ACTION_PERMISSION_KEYS.contact,
    ACTION_PERMISSION_VALUES.update,
  );
  const canAddEmail = hasActionPermission(
    ACTION_PERMISSION_KEYS.contactEmail,
    ACTION_PERMISSION_VALUES.create,
  );
  const canDelete = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterSurvey,
    ACTION_PERMISSION_VALUES.delete,
  );
  const connections = getMatterPartyConnections(matter)
    .filter((connection) => connection.state === "active")
    .map((connection) =>
      toMatterContactConnectionPresentation(connection, matter.id),
    );
  const clientConnection = connections.find((connection) =>
    isClientPartyConnectionLabel(connection.label),
  );
  const hasClientEmail =
    clientConnection?.contact.emails.some((email) =>
      Boolean(email.address?.trim()),
    ) ?? false;
  const clientName = clientConnection
    ? formatContactsName(clientConnection.contact) || "this client"
    : "this client";
  const portalAccess = useContactPortalAccess(
    clientConnection?.contactId ?? "",
    canView,
  );
  const hasPortalAccess = portalAccess.data?.data.activeConnection === true;
  const invitation = portalAccess.data?.data;
  const invitationPending =
    invitation?.status === "pending" &&
    (!invitation.expiresAt || new Date(invitation.expiresAt) > new Date());
  const { data, isLoading, isError, refetch } = useMatterSurveys(
    canView ? matter.id : "",
  );
  const requestedSurvey = useMatterSurvey(
    requestedSurveyId ?? "",
    matter.id,
    canView && Boolean(requestedSurveyId),
  );
  const cancelSurvey = useCancelMatterSurvey(matter.id);
  const deleteSurvey = useDeleteMatterSurvey(matter.id);
  const [createOpen, setCreateOpen] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [openingInvite, setOpeningInvite] = useState(false);
  const [editContactOpen, setEditContactOpen] = useState(false);
  const [openingEditContact, setOpeningEditContact] = useState(false);
  const [surveyAction, setSurveyAction] = useState<MatterSurveyAction | null>(
    null,
  );
  const [resultsSurvey, setResultsSurvey] = useState<MatterSurvey | null>(null);
  const [startFilling, setStartFilling] = useState(false);
  const surveys = useMemo(() => data?.data ?? [], [data?.data]);
  const openedSurveyId = useRef<string | null>(null);

  useEffect(() => {
    if (!requestedSurveyId || openedSurveyId.current === requestedSurveyId)
      return;
    const requested =
      surveys.find((survey) => survey.id === requestedSurveyId) ??
      requestedSurvey.data?.data;
    if (requested) {
      openedSurveyId.current = requestedSurveyId;
      setResultsSurvey(requested);
    }
  }, [requestedSurveyId, requestedSurvey.data?.data, surveys]);

  if (!canView) return null;

  const openInvite = async () => {
    if (!clientConnection || openingInvite) return;
    setOpeningInvite(true);
    try {
      await queryClient.fetchQuery(
        retrieveContactQueryOptions(clientConnection.contactId),
      );
      setInviteOpen(true);
    } catch {
      toast.error("Could not load this contact. Please try again.");
    } finally {
      setOpeningInvite(false);
    }
  };

  const openEditContact = async () => {
    if (!clientConnection || openingEditContact) return;
    setOpeningEditContact(true);
    try {
      await queryClient.fetchQuery(
        retrieveContactQueryOptions(clientConnection.contactId),
      );
      setEditContactOpen(true);
    } catch {
      toast.error("Could not load this contact. Please try again.");
    } finally {
      setOpeningEditContact(false);
    }
  };

  const recipientFor = (survey: MatterSurvey) =>
    survey.connections.find(
      (connection) => connection.id === survey.recipientConnectionId,
    );

  const columns = matterSurveyTableColumns({
    recipientName: (survey) => {
      const recipient = recipientFor(survey);
      return recipient?.contact
        ? formatContactsName(recipient.contact) || "Unknown recipient"
        : "Unknown recipient";
    },
    invitePending: (survey) =>
      Boolean(
        invitationPending &&
          !hasPortalAccess &&
          clientConnection?.contactId ===
            recipientFor(survey)?.targetObjectId &&
          ["pending", "in_progress", "reopened"].includes(survey.status),
      ),
    onView: (survey) => {
      setStartFilling(false);
      setResultsSurvey(survey);
    },
  });

  return (
    <>
      <Card className="h-min">
        <CardContent>
          <div className="mt-4 mb-3 flex h-10 items-center gap-x-2">
            <Text variant="body" color="primary" className="font-semibold">
              Client surveys
            </Text>
            {canCreate ? (
              <Button
                variant="outline"
                className="ml-auto flex"
                onClick={() => {
                  setCreateOpen(true);
                }}
                disabled={!clientConnection}
              >
                <PlusCircle className="size-4" /> Create survey
              </Button>
            ) : null}
          </div>
          {isError ? (
            <div className="py-6 text-sm">
              <p className="text-destructive">Could not load client surveys.</p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => void refetch()}
              >
                Retry
              </Button>
            </div>
          ) : (
            <div className="min-h-64">
              {invitationPending && !hasPortalAccess ? (
                <div
                  role="status"
                  className="mb-4 flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
                >
                  <CircleAlert
                    className="mt-0.5 size-4 shrink-0"
                    aria-hidden="true"
                  />
                  <div>
                    <p className="font-semibold">
                      Portal invitation not accepted
                    </p>
                    <p className="mt-1">
                      {clientName}
                      {invitation.invitedEmail
                        ? ` (${invitation.invitedEmail})`
                        : ""}{" "}
                      can answer these surveys after accepting the invitation.
                      You can complete them here in the meantime.
                    </p>
                  </div>
                </div>
              ) : null}
              {clientConnection && portalAccess.isPending ? (
                <Skeleton
                  role="status"
                  aria-label="Loading client portal access"
                  className="mb-4 h-16 w-full rounded-lg"
                />
              ) : !clientConnection ||
                (!hasPortalAccess && !invitationPending) ? (
                <div className="bg-muted/40 border-border mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4">
                  <div>
                    <p className="text-muted-foreground text-sm">
                      {!clientConnection
                        ? "Add a client contact before creating a survey."
                        : portalAccess.isError
                          ? "Could not check client portal access."
                          : `You can complete surveys for ${clientName} here. Invite them to the portal if they should answer themselves.`}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {clientConnection && portalAccess.isError ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => void portalAccess.refetch()}
                      >
                        Retry
                      </Button>
                    ) : clientConnection &&
                      canAddEmail &&
                      !hasClientEmail &&
                      clientConnection.contact.type === "individual" &&
                      !invitationPending ? (
                      <>
                        {canInvite ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={openingInvite}
                            onClick={() => void openInvite()}
                          >
                            <Send className="size-4" /> Add email & invite
                          </Button>
                        ) : null}
                        {canEditContact ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={openingEditContact}
                            onClick={() => void openEditContact()}
                          >
                            <Pencil className="size-4" /> Edit contact
                          </Button>
                        ) : null}
                      </>
                    ) : clientConnection &&
                      canInvite &&
                      hasClientEmail &&
                      clientConnection.contact.type === "individual" &&
                      !invitationPending ? (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={openingInvite}
                        onClick={() => void openInvite()}
                      >
                        <Send className="size-4" /> Invite to portal
                      </Button>
                    ) : null}
                  </div>
                </div>
              ) : null}
              <DataTable
                columns={columns}
                data={surveys}
                isLoading={isLoading}
                onRowClick={(row) => {
                  setStartFilling(false);
                  setResultsSurvey(row.original);
                }}
                renderRowContextMenu={(row, trigger) => {
                  const survey = row.original;
                  const isActive = [
                    "pending",
                    "in_progress",
                    "reopened",
                  ].includes(survey.status);
                  return (
                    <ContextMenu
                      entityName="survey"
                      trigger={trigger}
                      delete={
                        canDelete
                          ? {
                              onSelect: () => {
                                setSurveyAction({ kind: "delete", survey });
                              },
                            }
                          : false
                      }
                    >
                      <ContextMenuItem
                        onSelect={() => {
                          setStartFilling(false);
                          setResultsSurvey(survey);
                        }}
                      >
                        <Eye className="size-4" /> View survey
                      </ContextMenuItem>
                      {isActive && canDelete ? (
                        <ContextMenuItem
                          onSelect={() => {
                            setSurveyAction({ kind: "cancel", survey });
                          }}
                        >
                          <Ban className="size-4" /> Cancel survey
                        </ContextMenuItem>
                      ) : null}
                    </ContextMenu>
                  );
                }}
                bodyRowClassName="cursor-pointer"
                tableName={tanstackTableNames.matterSurveys}
                noResultsMessage="No client surveys found."
                hideSearchBar
                hideVisiibilityDropdown
              />
            </div>
          )}
        </CardContent>
      </Card>
      {clientConnection && inviteOpen ? (
        <ContactPortalInviteDialog
          contactId={clientConnection.contactId}
          open={inviteOpen}
          onOpenChange={setInviteOpen}
        />
      ) : null}
      {clientConnection && editContactOpen ? (
        <EditContactModal
          open={editContactOpen}
          setOpen={setEditContactOpen}
          activeContactId={clientConnection.contactId}
          hasEditPermission={canEditContact}
          matterContactConnection={clientConnection}
          matterId={matter.id}
        />
      ) : null}
      {clientConnection ? (
        <MatterSurveyCreateModal
          open={createOpen}
          onOpenChange={setCreateOpen}
          matterId={matter.id}
          clientConnection={clientConnection}
          portalEmail={invitation?.portalAccess?.accountEmail?.trim()}
          invitedEmail={invitationPending ? invitation.invitedEmail : undefined}
          onCreated={(survey) => {
            if (hasPortalAccess) {
              toast.success(
                "Survey created. It is available in the client's portal.",
              );
            } else if (invitationPending) {
              toast.success(
                "Survey created; no survey message was sent. It will appear in the portal after the client accepts their invitation.",
              );
            } else {
              toast.success(
                "Survey created. You can save draft answers, then invite the client to submit through the portal.",
              );
              setStartFilling(true);
              setResultsSurvey(survey);
            }
          }}
        />
      ) : null}
      <MatterSurveyResultsDialog
        survey={resultsSurvey}
        startFilling={startFilling}
        canFill={canCreate}
        canDelete={canDelete}
        onAction={(action) => {
          setResultsSurvey(null);
          setSurveyAction(action);
        }}
        onOpenChange={(open) => {
          if (!open) setResultsSurvey(null);
        }}
      />
      <MatterSurveyActionDialog
        action={surveyAction}
        onClose={() => {
          setSurveyAction(null);
        }}
        onConfirm={async ({ kind, survey }) => {
          if (kind === "cancel") {
            await cancelSurvey.mutateAsync(survey.id);
          } else {
            await deleteSurvey.mutateAsync(survey.id);
          }
        }}
      />
    </>
  );
}

export function MatterInformationSurveySection({
  matter,
  requestedSurveyId,
}: {
  matter: Matter;
  requestedSurveyId?: string;
}) {
  return (
    <ErrorBoundary fallback={<ServerErrorFallbackCard />}>
      <MatterInformationSurveySectionContent
        matter={matter}
        requestedSurveyId={requestedSurveyId}
      />
    </ErrorBoundary>
  );
}
